import { HeadcountActualEmployeesDocument } from 'generated/resources/graphql'

import { useLazyQuery } from 'modules/core/hooks/useQuery'
import {
  type PaginatedHeadcountResult,
  type HeadcountPaginationInfo,
} from 'modules/planning/types/headcount'
import { mapEmployeeToHeadcountRow } from 'modules/planning/hooks/headcount/mappers/headcountActualEmployeeMapper'

const PAGE_SIZE = 20

interface UseHeadcountActualEmployeesParams {
  planningPeriodId: string
}

const useHeadcountActualEmployees = ({ planningPeriodId }: UseHeadcountActualEmployeesParams) => {
  const [fetchActualEmployeesQuery] = useLazyQuery(HeadcountActualEmployeesDocument)

  const fetchActualEmployees = async (
    pagination?: HeadcountPaginationInfo,
    selfAndDescendantsOfEmployeeId?: string
  ): Promise<PaginatedHeadcountResult> => {
    const after = pagination?.endCursor ?? null

    const { data } = await fetchActualEmployeesQuery({
      variables: {
        first: PAGE_SIZE,
        after,
        selfAndDescendantsOfEmployeeId,
        planningPeriodId,
      },
      fetchPolicy: 'no-cache',
    })

    const connection = data?.employees.employeesConnection
    const newRecords = (connection?.edges ?? []).map(mapEmployeeToHeadcountRow)
    const totalCount = connection?.totalCount ?? 0
    const pageInfo = connection?.pageInfo

    const currentPage = (pagination?.currentPage ?? 0) + 1
    const pagesCount = Math.ceil(totalCount / PAGE_SIZE)

    return {
      records: newRecords,
      paginationInfo: {
        total: totalCount,
        perPage: PAGE_SIZE,
        currentPage,
        pagesCount,
        hasMore: pageInfo?.hasNextPage ?? false,
        endCursor: pageInfo?.endCursor ?? null,
      },
    }
  }

  return { fetchActualEmployees }
}

export default useHeadcountActualEmployees
